/**
 * EvalTaskPage (`/evals/groups/:slug/tasks/:taskSlug`) — one task.
 *
 * The unscoped route shows the task across all variants. Matrix cells
 * add `?variant=` so the same page becomes a task + variant history
 * view with pair-specific stats and runs. Row click → run detail.
 */

import { Navigate, Link, useParams, useSearchParams } from 'react-router-dom'
import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'

import { DraftMarker } from '@/components/evals/DraftMarker'
import { EvalBreadcrumb } from '@/components/evals/EvalBreadcrumb'
import { RunsTable } from '@/components/evals/RunsTable'
import { ScoreHistoryChart, VariantHistoryChart } from '@/components/evals/ScoreHistoryChart'
import { formatRelative, formatScore, formatVariant } from '@/components/evals/format'
import { StatRow, type Stat } from '@/components/evals/StatRow'
import {
  runsForVariant,
  scoredRuns,
  scoreHistoryByVariant,
  scoreHistoryForRuns,
  selectedTaskVariant,
  summarizeRuns,
} from '@/components/evals/taskVariant'
import { ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useEvalTask, useRenameEvalTask } from '@/hooks/useEvals'
import { RenameDialog } from '@/components/shared/RenameDialog'
import type { EvalRunSummary, EvalTaskDetailResponse } from '@/api/generated/model'

function taskStats(task: EvalTaskDetailResponse, runs: EvalRunSummary[], variant: string | null): Stat[] {
  if (variant === null) {
    return [
      { label: 'Headline score', value: formatScore(task.score) },
      { label: 'Best score', value: formatScore(task.best_score) },
      { label: 'Runs', value: task.run_count.toString() },
      { label: 'Variants', value: task.variant_count.toString() },
      { label: 'Last run', value: formatRelative(task.last_run_at) },
    ]
  }

  // Reference runs are listed in the table below but never scored.
  const scored = scoredRuns(runs)
  const summary = summarizeRuns(scored)
  return [
    { label: 'Headline score', value: formatScore(summary.score) },
    { label: 'Best score', value: formatScore(summary.bestScore) },
    { label: 'Runs', value: scored.length.toString() },
    { label: 'Last run', value: formatRelative(summary.lastRunAt) },
  ]
}

function TaskDescription({ task }: { task: string }): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Task</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="whitespace-pre-wrap text-sm text-foreground">{task}</p>
      </CardContent>
    </Card>
  )
}

/**
 * The task's grading rubric — plain-English yes/no checks extracted from the
 * test's assertions by the benchmark inventory (or pushed by a pipeline).
 */
function TaskVerifiers({
  criteria,
}: {
  criteria: EvalTaskDetailResponse['criteria']
}): React.JSX.Element | null {
  if (criteria.length === 0) return null
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Verifiers</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-2">
          {criteria.map((criterion, index) => (
            <li
              key={typeof criterion.id === 'string' ? criterion.id : index}
              className="flex items-baseline gap-3 text-sm"
            >
              <span className="font-mono text-xs text-muted-foreground">{index + 1}</span>
              <span>
                {typeof criterion.question === 'string' ? criterion.question : ''}
                {typeof criterion.rationale === 'string' && criterion.rationale !== '' && (
                  <span className="text-muted-foreground">: {criterion.rationale}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}

/**
 * Minimal TS/JSON token colorizer for the seed-data block. The content is
 * machine-cut object literals + template-literal markdown docs, so a small
 * regex pass covers it — no grammar library needed. Comments carry the
 * provenance headers; template literals carry the seed documents.
 */
const SEED_TOKEN_RE =
  /(\/\/[^\n]*)|(`(?:[^`\\]|\\[\s\S])*`|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|\b(export|const|let|var|function|async|return|new|true|false|null|undefined)\b|([A-Za-z_$][\w$]*)(?=\s*:)|(-?\d+(?:\.\d+)?)/g

const SEED_TOKEN_CLASSES = [
  'italic text-muted-foreground', // comment (provenance header)
  'text-success', // string / template-literal document
  'font-medium text-foreground', // keyword
  'text-info', // property key
  'text-info', // number
]

function highlightSeed(src: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  let last = 0
  for (const match of src.matchAll(SEED_TOKEN_RE)) {
    if (match.index > last) nodes.push(src.slice(last, match.index))
    const groupIndex = match.slice(1).findIndex((g) => typeof g === 'string')
    nodes.push(
      <span key={match.index.toString()} className={SEED_TOKEN_CLASSES[groupIndex]}>
        {match[0]}
      </span>
    )
    last = match.index + match[0].length
  }
  if (last < src.length) nodes.push(src.slice(last))
  return nodes
}

/**
 * The verbatim mock/seed source the benchmark inventory cut from the test —
 * the starting world state graders otherwise can't see. Collapsed by
 * default; it's raw TS/JSON and can run long.
 */
function TaskSeedData({ seedData }: { seedData: string }): React.JSX.Element | null {
  if (seedData === '') return null
  return (
    <Card>
      <Collapsible>
        <CollapsibleTrigger className="group flex w-full items-center justify-between px-6 py-4 text-left">
          <CardTitle className="text-sm font-medium text-muted-foreground">Seed data</CardTitle>
          <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed text-foreground">
              {highlightSeed(seedData)}
            </pre>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

interface TaskHistoryProps {
  displayName: string
  allRuns: EvalRunSummary[]
  selectedRuns: EvalRunSummary[]
  variant: string | null
}

function TaskHistory({ displayName, allRuns, selectedRuns, variant }: TaskHistoryProps): React.JSX.Element | null {
  // Reference runs draw as lines on the GROUP chart only; here they're just
  // dropped so they don't fake a datapoint.
  const series = scoreHistoryByVariant(scoredRuns(allRuns))
  if (variant === null) {
    return <VariantHistoryChart title="Task score over time" series={series} />
  }
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ScoreHistoryChart
        title={`${displayName} · ${formatVariant(variant)} over time`}
        points={scoreHistoryForRuns(scoredRuns(selectedRuns))}
        caption="This variant's score at each run date."
      />
      <VariantHistoryChart
        title={`${displayName} across all variants over time`}
        series={series}
      />
    </div>
  )
}

interface TaskContentProps {
  task: EvalTaskDetailResponse
  groupSlug: string
  taskSlug: string
  variant: string | null
}

/** Header pencil (TRA-947); the optimistic hook updates the /evals chip too. */
function TaskRenameButton({
  groupSlug,
  taskSlug,
  displayName,
}: {
  groupSlug: string
  taskSlug: string
  displayName: string
}): React.JSX.Element {
  const { rename } = useRenameEvalTask(groupSlug, taskSlug)
  return (
    <RenameDialog
      title="Rename task"
      slug={taskSlug}
      displayName={displayName}
      maxLength={120}
      onSave={rename}
    />
  )
}

function TaskContent({ task, groupSlug, taskSlug, variant }: TaskContentProps): React.JSX.Element {
  const runs = runsForVariant(task.runs, variant)
  const title = variant === null ? task.display_name : `${task.display_name} · ${formatVariant(variant)}`
  return (
    <>
      <PageHeader
        className="group/rename"
        title={title}
        description={
          variant !== null
            ? `Only runs for the ${formatVariant(variant)} variant.`
            : task.status === 'draft'
              ? 'Draft task. It becomes active when its regression-test PR merges.'
              : 'All variants for this task.'
        }
        actions={
          <>
            <TaskRenameButton
              groupSlug={groupSlug}
              taskSlug={taskSlug}
              displayName={task.display_name}
            />
            {task.status === 'draft' && <DraftMarker prUrl={task.pr_url} />}
            {variant !== null && (
              <Button asChild variant="outline">
                <Link to={`/evals/groups/${groupSlug}/tasks/${taskSlug}`}>All variants</Link>
              </Button>
            )}
          </>
        }
      />
      <StatRow stats={taskStats(task, runs, variant)} />
      <TaskHistory displayName={task.display_name} allRuns={task.runs} selectedRuns={runs} variant={variant} />
      <TaskDescription task={task.task} />
      <TaskSeedData seedData={task.seed_data} />
      <TaskVerifiers criteria={task.criteria} />
      <RunsTable groupSlug={groupSlug} taskSlug={taskSlug} runs={runs} />
    </>
  )
}

function taskPageTitle(
  task: EvalTaskDetailResponse | undefined,
  taskSlug: string | undefined,
  variant: string | null
): string {
  if (!task) return taskSlug ?? ''
  return variant === null ? task.display_name : `${task.display_name} · ${formatVariant(variant)}`
}

interface TaskBreadcrumbProps {
  task: EvalTaskDetailResponse | undefined
  groupSlug: string | undefined
  title: string
}

function TaskBreadcrumb({ task, groupSlug, title }: TaskBreadcrumbProps): React.JSX.Element {
  return (
    <EvalBreadcrumb
      crumbs={[
        { label: 'Eval Groups', to: '/evals' },
        {
          label: task?.group_display_name ?? groupSlug ?? '',
          to: groupSlug ? `/evals/groups/${groupSlug}` : undefined,
        },
        { label: title },
      ]}
    />
  )
}

function TaskPageSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-3">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  )
}

export default function EvalTaskPage(): React.JSX.Element {
  const { slug, taskSlug } = useParams<{ slug: string; taskSlug: string }>()
  const [searchParams] = useSearchParams()
  const variant = selectedTaskVariant(searchParams)
  const { data: task, isLoading, isError, error } = useEvalTask(slug, taskSlug)
  const title = taskPageTitle(task, taskSlug, variant)

  return (
    <PageContainer className="space-y-6">
      <TaskBreadcrumb task={task} groupSlug={slug} title={title} />
      {isLoading && <TaskPageSkeleton />}
      {isError && slug !== undefined && getApiErrorStatus(error) === 404 && (
        <Navigate to={`/evals/groups/${slug}`} replace />
      )}
      {isError && getApiErrorStatus(error) !== 404 && (
        <p className="text-sm text-destructive">
          Failed to load task: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}
      {!isLoading && !isError && task && slug && taskSlug && (
        <TaskContent task={task} groupSlug={slug} taskSlug={taskSlug} variant={variant} />
      )}
    </PageContainer>
  )
}
